import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { axiosServiceInstance } from '@onesource/services';
import { Link, useLocation } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  RiArrowLeftLine,
  RiExternalLinkLine,
  RiFolderLine,
  RiFolderOpenFill,
  RiLockLine,
  RiLockUnlockLine,
} from 'react-icons/ri';
import { DraftBadge } from 'src/components/draft-badge';

import { collectFolderIds } from './collect-folder-ids';
import type { SidebarItem, SidebarSubItem } from './types';

const isPathActive = (pathname: string, path: string): boolean => pathname === path || pathname.startsWith(`${path}/`);

// All folders (ids of items with children) at any depth — the list here is short
// enough (a handful of spaces) that starting collapsed just costs an extra tap.
interface OttGenerationResponse {
  ott: string;
  redirectUrl: string;
  expiresIn: number;
}

interface MobileSubItemLabelProps {
  subItem: SidebarSubItem;
  onClose: () => void;
}

const MobileSubItemLabel = ({ subItem, onClose }: MobileSubItemLabelProps) => {
  const location = useLocation();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  // A shortcut's route belongs to another menu, so it never shows as this menu's active row.
  const isActive = !subItem.isShortcut && !!subItem.path && isPathActive(location.pathname, subItem.path);
  const Icon = subItem.icon;

  // Program-group heading — a static, non-interactive section label.
  if (subItem.isGroupLabel) {
    return (
      <Typography
        component="span"
        sx={{
          display: 'block',
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'grey.600',
          py: 1,
        }}
        dangerouslySetInnerHTML={{ __html: subItem.label }}
      />
    );
  }

  const handleExternalAppClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!subItem.externalApp || loading) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosServiceInstance.post<OttGenerationResponse>('/auth/generate-ott/user', {
        targetApp: subItem.externalApp.appId,
        redirectUrl: subItem.externalApp.url,
        ...(subItem.externalApp.redirectTo ? { redirectTo: subItem.externalApp.redirectTo } : {}),
      });
      const anchor = document.createElement('a');
      anchor.href = response.data.redirectUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      console.error('Failed to generate OTT:', error);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {Icon && (
          <Box
            component="span"
            sx={{ display: 'flex', flexShrink: 0, color: subItem.isLocked ? '#B3B3B3' : 'grey.600' }}
          >
            <Icon size={16} />
          </Box>
        )}
        <Typography
          component="span"
          noWrap
          sx={{
            fontSize: 15,
            fontWeight: subItem.isLocked ? 400 : isActive ? 600 : 500,
            color: subItem.isLocked ? '#B3B3B3' : 'text.primary',
          }}
          dangerouslySetInnerHTML={{ __html: subItem.label }}
        />
        {subItem.isDraft && <DraftBadge sx={{ flexShrink: 0 }} />}
      </Box>
      {loading ? (
        <CircularProgress size={14} sx={{ ml: 1 }} />
      ) : subItem.externalApp ? (
        <RiExternalLinkLine size={14} color={theme.palette.grey[900]} style={{ marginLeft: 8 }} />
      ) : (
        subItem.isLocked !== undefined &&
        (subItem.isLocked ? (
          <RiLockLine size={14} color={theme.palette.grey[900]} style={{ marginLeft: 8 }} />
        ) : (
          <RiLockUnlockLine size={14} color={theme.palette.grey[900]} style={{ marginLeft: 8 }} />
        ))
      )}
    </Box>
  );

  // Progress-locked row (prerequisite unfinished): show why and don't navigate,
  // matching the module cards in the space body.
  if (subItem.lockMessage) {
    return (
      <Tooltip title={subItem.lockMessage} placement="top" arrow>
        <Box sx={{ cursor: 'not-allowed' }}>{content}</Box>
      </Tooltip>
    );
  }

  // Folders (spaces with sub-spaces) navigate to their space home like the /apps card.
  if (subItem.externalApp) {
    return (
      <Box onClick={handleExternalAppClick} sx={{ cursor: loading ? 'default' : 'pointer' }}>
        {content}
      </Box>
    );
  }

  if (subItem.path) {
    return (
      <Link to={subItem.path} onClick={onClose} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        {content}
      </Link>
    );
  }

  return content;
};

// Width of the horizontal connector tick; doubles as the group's left padding so
// the tick spans exactly from the vertical guide line to the row.
const CONNECTOR_WIDTH = 12;

// The tick joining the group's vertical guide line to a nested row (forms an L
// with it). Only rows inside a group — depth > 0 — get one.
const connectorSx = {
  content: '""',
  position: 'absolute',
  left: -CONNECTOR_WIDTH,
  top: '50%',
  width: CONNECTOR_WIDTH,
  height: '1px',
  bgcolor: 'divider',
} as const;

const renderMobileTreeItem = (subItem: SidebarSubItem, onClose: () => void, depth = 0) => {
  const children = subItem.children ?? [];
  const isGroupLabel = !!subItem.isGroupLabel;

  return (
    <TreeItem
      key={subItem.id}
      nodeId={subItem.id}
      label={<MobileSubItemLabel subItem={subItem} onClose={onClose} />}
      sx={{
        // Extra space above each group header separates one program group from
        // the next (`:first-of-type` keeps the top group flush with the panel).
        ...(isGroupLabel && { mt: 1.75, '&:first-of-type': { mt: 0 } }),
        // Indent a group's apps/spaces under their (non-collapsible) header.
        ...(depth === 0 && !isGroupLabel && { ml: 1.5 }),
        '& > .MuiTreeItem-content': {
          position: 'relative',
          py: 0,
          px: 1,
          borderRadius: 1,
          // Group headers are static labels — no hover/selection affordance.
          ...(isGroupLabel
            ? { cursor: 'default', '&:hover': { bgcolor: 'transparent' } }
            : {
                '&:hover': { bgcolor: 'action.hover' },
                '&.Mui-selected, &.Mui-selected.Mui-focused, &.Mui-selected:hover': { bgcolor: 'grey.200' },
              }),
          ...(depth > 0 && { '&::before': connectorSx }),
        },
        // Leaf rows collapse the empty expand-icon slot so the label sits next to
        // the connector tick (folders keep the slot for their expand icon).
        ...(children.length === 0 && {
          '& > .MuiTreeItem-content .MuiTreeItem-iconContainer': { width: 0, marginRight: 0 },
        }),
      }}
    >
      {children.map(child => renderMobileTreeItem(child, onClose, depth + 1))}
    </TreeItem>
  );
};

interface MobileSubmenuPanelProps {
  item: SidebarItem;
  onBack: () => void;
  onClose: () => void;
}

export const MobileSubmenuPanel = ({ item, onBack, onClose }: MobileSubmenuPanelProps) => {
  const children = useMemo(() => item.children ?? [], [item.children]);

  const [expanded, setExpanded] = useState<string[]>(() => collectFolderIds(children));
  useEffect(() => {
    setExpanded(collectFolderIds(children));
  }, [children]);

  if (children.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: '#fafafa',
        display: 'flex',
        flexDirection: 'column',
        py: 3,
        px: 2,
        zIndex: 10,
      }}
    >
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: 'grey.700' }}>
          <RiArrowLeftLine size={18} />
        </IconButton>
        {!item.hideTitle && (
          <Typography
            component="span"
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: 'text.primary',
              bgcolor: '#FFEEE8',
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
            }}
          >
            {item.label}
          </Typography>
        )}
      </Box>

      {/* Sub-items */}
      <TreeView
        aria-label={`${item.label} navigation`}
        expanded={expanded}
        onNodeToggle={(_event, nodeIds) => setExpanded(nodeIds)}
        // Folder as the expand indicator: filled+open when expanded, outline
        // closed when collapsed (replaces the arrow chevrons).
        defaultCollapseIcon={<RiFolderOpenFill size={18} />}
        defaultExpandIcon={<RiFolderLine size={18} />}
        defaultEndIcon={null}
        sx={{
          '& .MuiTreeItem-group': {
            ml: 1.5,
            pl: `${CONNECTOR_WIDTH}px`,
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {children.map(subItem => renderMobileTreeItem(subItem, onClose))}
      </TreeView>
    </Box>
  );
};
